import React, {ChangeEvent, useMemo, useState} from 'react';

import {Avatar} from 'src/components/avatar/avatar';
import {filterUsers} from 'src/modules/admin/user-role-management/utils/filterUsers';
import {searchUsers, searchUsersSuccess} from 'src/openapi/apis/UserControllerApi';
import {User} from 'src/openapi/models/User';
import {UserRoles} from 'src/openapi/models/UserRoles';
import {useAppDispatch} from 'src/store';

import {Autosuggest, AutosuggestItem, AutosuggestNoResults} from '@pb/kit-component-autosuggest-v2';
import {Group, Item} from '@pb/kit-component-group';

type Props = {
    onSelectedUser: (user: User) => void,
    userRolesData: UserRoles[],
};

const MIN_SUGGESTION_LENGTH = 3;

export const UserAutosuggest = ({onSelectedUser, userRolesData}: Props) => {
    const dispatch = useAppDispatch();
    const [suggestUsers, setSuggestUsers] = useState<Array<User>>([]);
    const [value, setValue] = useState<string>('');

    const onSearchUsers = async (query: string) => {
        if (query.length >= MIN_SUGGESTION_LENGTH) {
            const response = await dispatch(searchUsers({searchPhrase: query}));

            if (!response.payload.error && response.type === searchUsersSuccess.type) {
                setSuggestUsers(filterUsers(response.payload.data, userRolesData));
            }
            return;
        }
        setSuggestUsers([]);
    };

    const onChangeQuery = (event: ChangeEvent<HTMLInputElement>) => {
        const inputQuery = event.target.value;

        setValue(inputQuery);
        onSearchUsers(inputQuery);
    };

    const onClear = () => {
        setSuggestUsers([]);
        setValue('');
    };

    const onItemSelect = (user: User) => {
        onClear();
        onSelectedUser(user as User);
    };

    const users = useMemo(() => suggestUsers.map(user => (
        <AutosuggestItem
            key={user.email}
            value={user}
            caption={(
                <Group alignItems="center">
                    <Avatar user={user}/>
                    <Item>{user.firstName} {user.lastName}</Item>
                </Group>
            )}
            disabled={false}
        />
    )), [suggestUsers]);

    return (
        <Group direction="v" style={{width: '50%'}}>
            <Group gap="xs">
                <Item><strong>New User</strong></Item>
            </Group>
            <Autosuggest
                placeholder="Search for user"
                value={value}
                onChange={onChangeQuery}
                onItemSelect={onItemSelect}
                onClear={onClear}>
                {users.length ? users : <AutosuggestNoResults/>}
            </Autosuggest>
        </Group>
    );
};
